import { EventEmitter, Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { v4 as uuidv4 } from 'uuid';
import { Observable, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Service to manage WebSocket connections specific for digital signature
 * Based on WebsocketService but with improvements for communication with signature servers
 */
@Injectable({
  providedIn: 'root',
})
export class SigSocketService {
  public webSocketError: EventEmitter<string> = new EventEmitter();
  public webSocketSuccess: EventEmitter<boolean> = new EventEmitter();
  private webSocket$: WebSocketSubject<any>;
  private socketId: string = uuidv4();
  private operationId: string = '';
  private connectionStatus$: Subject<boolean> = new Subject<boolean>();

  // Base URL for WebSocket connection
  private wsBaseUrl: string = 'wss://sigsocketspro.apps.signa.run';

  constructor() { }

  /**
   * Gets the connection status as Observable
   * @returns Observable that emits true when the connection is active and false when closed
   */
  public getConnectionStatus(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }

  /**
   * Activates and creates a websocket with RxJS WebSocketSubject
   * @param operationId Operation ID to connect
   * @param clientType Client type (optional, default is 1)
   * @returns WebSocketSubject that can be subscribed to
   */
  public activateWebSocket(operationId: string, clientType: number = 1): WebSocketSubject<any> {
    this.operationId = operationId;
    this.validateWebSocket();
    const wsUrl = `${this.wsBaseUrl}/ivcert?id=${this.socketId}&group=${this.operationId}&type=${clientType}`;

    this.webSocket$ = webSocket({
      url: wsUrl,
      openObserver: {
        next: () => {
          this.webSocketSuccess.emit(true);
          this.connectionStatus$.next(true);
        },
      },
      closeObserver: {
        next: () => {
          this.connectionStatus$.next(false);
          this.handleClose();
        },
      },
    });

    this.webSocket$
      .pipe(
        catchError((err) => {
          this.webSocketError.emit('message_webSocket_error');
          this.connectionStatus$.next(false);
          throw err;
        })
      )
      .subscribe();

    return this.webSocket$;
  }

  public validateWebSocket() {
    const url = window.location.href;

    if (url.includes('.qatest') || url.includes('.test')) {
      this.wsBaseUrl = 'wss://sigsocketspre.apps.signa.run';
    } else {
      this.wsBaseUrl = 'wss://sigsocketspro.apps.signa.run';
    }
  }

  /**
   * Sends a message through the websocket
   * @param message Message to send
   * @param targetId Target recipient ID (optional)
   */
  public sendMessage(message: any, targetId?: string): void {
    if (!this.webSocket$) {
      return;
    }

    const messageObj = {
      Message: JSON.stringify({
        Group: this.operationId,
        Id: this.socketId,
        Type: 3,
        SendToParent: message,
        TargetId: targetId,
      }),
      SubType: 0,
      Type: 3,
      WebSocketId: targetId || null,
    };

    try {
      this.webSocket$.next(messageObj);
    } catch (error: unknown) {
      console.error('SigSocket: Error sending message', error instanceof Error ? error.message : 'Unknown error');
    }
  }

  /**
   * Returns the current WebSocketSubject
   * @returns Current WebSocketSubject
   */
  public getWebSocket(): WebSocketSubject<any> {
    return this.webSocket$;
  }

  private handleClose(): void {
    if (this.webSocket$) {
      const closeMessage = {
        Message: JSON.stringify({
          Group: this.operationId,
          Id: this.socketId,
          Type: 4,
          SendToParent: null,
        }),
        SubType: 0,
        Type: 4,
        WebSocketId: null,
      };

      try {
        this.webSocket$.next(closeMessage);
      } catch (error: unknown) {
        console.warn(
          'SigSocket: Could not send close message',
          error instanceof Error ? error.message : 'Unknown error'
        );
      }
    }
  }

  public closeWebSocket(): void {
    if (this.webSocket$) {
      this.handleClose();
      this.webSocket$.complete();
      this.webSocket$ = null;
    }
  }

  /**
   * Gets the current socket ID
   * @returns Current socket ID
   */
  public getSocketId(): string {
    return this.socketId;
  }

  /**
   * Gets the current operation ID
   * @returns Current operation ID
   */
  public getOperationId(): string {
    return this.operationId;
  }

  /**
   * Verifies if there is an active websocket connection
   * @returns true if there is an active connection, false otherwise
   */
  public isConnected(): boolean {
    return !!this.webSocket$;
  }
}
